import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import GameModal from '../GameModal/GameModal';
import {
  playCorrectSound,
  playIncorrectSound,
  playAudio,
  stopAudio,
  toggleAudio,
} from 'actions/audioActions';
import { s3bucketPublicURL, imagesURL } from 'config';
import { closeFeedback } from 'actions/gameActions';
import { setFeedbackCoords } from 'actions/flinkPlayActions';
import { debounce } from 'lodash';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';

const ANDROID_STANDARD_FEEDBACK_FALLBACK_MS = 6500;

const GameFeedback = ({
  versionType = '',
  placementLocaleCode = '',
  placementTranslate = null,
}) => {
  const solutionTranslate = useSelector(
    ({ flinkPlay, status }) => flinkPlay.solutionTranslate || status.translate
  );
  const translate = placementTranslate || solutionTranslate;
  const feedback = useSelector(({ game }) => game.feedback);
  const feedbackCoords = useSelector(
    ({ flinkPlay }) => flinkPlay.feedbackCoords
  );
  const contentFolder = useSelector(
    ({ game }) => game.currentActivity.activity.contentFolder
  );
  const audioState = useSelector(({ audio }) => audio);
  const dispatch = useDispatch();

  const contentPath = versionType 
    ? `${s3bucketPublicURL}/${contentFolder}/languageVersionData/${versionType}/` 
    : `${s3bucketPublicURL}/${contentFolder}/gamedata/`;
  const isCustomFeedback = feedback && !!(feedback.text || feedback.audio);
  const hasText = !!(feedback && feedback.text);

  let feedbackAudioSrc;
  let audioIcon;
  if (feedback && feedback.audio) {
    feedbackAudioSrc = contentPath + feedback.audio;
    audioIcon = `${imagesURL}/Images/Audio/${
      audioState.audioSrc === feedbackAudioSrc && audioState.isPlaying
        ? 'audiostop'
        : 'audio_icon1'
    }.png`;
  }

  const closeHandler = useCallback(() => {
    if (!feedback) return;
    dispatch(closeFeedback());
    if (feedbackAudioSrc) {
      stopAudio(feedbackAudioSrc);
    }
    feedback.onClose && feedback.onClose();
  }, [dispatch, feedback, feedbackAudioSrc]);

  useEffect(() => {
    if (!feedback) {
      return;
    }

    if (!isCustomFeedback) {
      dispatch(
        feedback.isCorrect
          ? playCorrectSound(placementLocaleCode || null)
          : playIncorrectSound(placementLocaleCode || null)
      );
      if (!feedback.onClose) return;

      // Preserve the existing iOS/web audio-ended behavior exactly. Android's
      // HTML5 audio path can lose the terminal event after a native play error,
      // which otherwise leaves mobile selected-only answers hidden forever.
      if (getMobileAppPlatform() !== 'android') {
        document.addEventListener('audioEnded', feedback.onClose, {
          once: true,
        });
        return;
      }

      let finished = false;
      let fallbackTimer = null;
      const finishStandardFeedback = () => {
        if (finished) return;
        finished = true;
        document.removeEventListener('audioEnded', finishStandardFeedback);
        if (fallbackTimer) window.clearTimeout(fallbackTimer);
        feedback.onClose();
      };

      document.addEventListener('audioEnded', finishStandardFeedback, { once: true });
      fallbackTimer = window.setTimeout(
        finishStandardFeedback,
        ANDROID_STANDARD_FEEDBACK_FALLBACK_MS
      );

      return () => {
        document.removeEventListener('audioEnded', finishStandardFeedback);
        if (fallbackTimer) window.clearTimeout(fallbackTimer);
      };
    }
    if (feedback.audio) {
      playAudio(contentPath + feedback.audio);
      document.addEventListener('audioEnded', closeHandler, { once: true });
    }

    return () => {
      feedback.onClose &&
        document.removeEventListener('audioEnded', feedback.onClose);
    };
  }, [
    feedback,
    dispatch,
    isCustomFeedback,
    contentFolder,
    hasText,
    contentPath,
    closeHandler,
    placementLocaleCode,
  ]);

  const debouncedDragHandler = React.useMemo(
    () => debounce((...args) => dispatch(setFeedbackCoords(...args)), 300),
    [dispatch]
  );

  if (!feedback || !feedback.text) return null;

  return (
    <GameModal
      draggable
      draggableProps={{
        position: feedbackCoords,
        onDrag: debouncedDragHandler,
      }}
      title={translate(950, 'Feedback')}
      buttons={[
        {
          clickHandler: (e) => toggleAudio(feedbackAudioSrc),
          dontShow: !feedback.audio,
          icon: audioIcon,
        },
        {
          clickHandler: closeHandler,
          icon: `${imagesURL}/Images/Help/Close50.png`,
        },
      ]}
    >
      <div style={{ fontSize: '2vmin' }}>{feedback.text}</div>
    </GameModal>
  );
};

export default GameFeedback;
